import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, InlineNotification, Modal, RadioButton, RadioButtonGroup, Select, SelectItem, TextArea, TextInput } from '@carbon/react';
import { api, ApiError } from '../api/client';
import { InlineError } from '../components/states';

type Sent = { soNguoi: number; soSocket: number; lapLai: boolean };
type Item = { id: string; at: string; by: string; userId?: string; kieu: 'chuChay' | 'popup'; noiDung: string; soLan?: number; soNguoi: number; soSocket: number };
type Repeat = { macDinh: number; toiDa: number };

const newKey = () => crypto.randomUUID();

/** A marquee line or a popup to the players online now (he B pushes it on their lobby socket). */
export function NoticePush() {
  const qc = useQueryClient();
  const history = useQuery({ queryKey: ['notices'], queryFn: () => api.get<{ items: Item[]; soLan: Repeat | null }>('/api/admin/content/notices') });
  const repeat = history.data?.soLan ?? null;
  const [kieu, setKieu] = useState<'chuChay' | 'popup'>('chuChay');
  const [noiDung, setNoiDung] = useState('');
  const [userId, setUserId] = useState('');
  // how many times the marquee line plays (null = he A default)
  const [soLan, setSoLan] = useState<number | null>(null);
  const lan = soLan ?? repeat?.macDinh;
  // one key per composed notice: a retry after "no answer" or a double click sends it once
  const [key, setKey] = useState(newKey);
  const [open, setOpen] = useState(false);
  const [done, setDone] = useState<Sent | null>(null);
  const max = kieu === 'popup' ? 500 : 300;
  const uid = userId.trim() ? Number(userId.trim()) : undefined;
  const bad = !noiDung.trim() ? 'Nhập nội dung' : noiDung.length > max ? `Tối đa ${max} ký tự` : userId.trim() && !(Number.isInteger(uid) && uid! > 0) ? 'Mã người chơi là số' : null;
  const edit = (f: () => void) => { f(); setKey(newKey()); setDone(null); };

  const push = useMutation({
    mutationFn: () => api.post<Sent>('/api/admin/content/notices', { key, kieu, noiDung: noiDung.trim(), soLan: kieu === 'chuChay' ? lan : undefined, userId: uid }),
    onSuccess: (r) => { setDone(r); setOpen(false); qc.invalidateQueries({ queryKey: ['notices'] }); },
  });
  const unknown = push.error instanceof ApiError && push.error.status === 503;

  return (
    <div className="split">
      <div className="stack">
        <p className="muted">Gửi ngay tới người đang online. Chữ chạy hiện ở dải chữ trên sảnh (người đang trong game thấy khi về sảnh); popup hiện giữa màn hình, có nút Đồng ý. Người vào sau không nhận: dùng Chữ chạy hoặc Popup vào game có lịch cho việc đó.</p>
        <RadioButtonGroup legendText="Loại" name="tb-kieu" valueSelected={kieu} onChange={(v) => edit(() => setKieu(v as 'chuChay' | 'popup'))}>
          <RadioButton id="tb-cc" labelText="Chữ chạy" value="chuChay" />
          <RadioButton id="tb-pu" labelText="Popup" value="popup" />
        </RadioButtonGroup>
        <TextArea id="tb-nd" labelText={`Nội dung (${noiDung.length}/${max})`} rows={4} value={noiDung} invalid={noiDung.length > max}
          invalidText={`Tối đa ${max} ký tự`} onChange={(e) => edit(() => setNoiDung(e.target.value))}
          helperText={kieu === 'chuChay' ? 'Màu: <color=#f5dd42>chữ</c>' : undefined} />
        {kieu === 'chuChay' && repeat ? (
          <Select id="tb-sl" labelText="Số lần chạy trên dải chữ" value={String(lan)} onChange={(e) => edit(() => setSoLan(Number(e.target.value)))}
            helperText="Chạy đủ số lần rồi dải chữ quay về dòng thường">
            {Array.from({ length: repeat.toiDa }, (_, i) => i + 1).map((n) => <SelectItem key={n} value={String(n)} text={n + ' lần'} />)}
          </Select>
        ) : null}
        <TextInput id="tb-u" labelText="Chỉ gửi cho một người chơi (mã người chơi, trống = mọi người đang online)" value={userId}
          onChange={(e) => edit(() => setUserId(e.target.value))} />
        <div className="row">
          <Button disabled={!!bad || push.isPending} onClick={() => { push.reset(); setOpen(true); }}>Gửi ngay</Button>
          {bad && noiDung ? <span className="muted">{bad}</span> : null}
        </div>
        {done ? (
          <InlineNotification kind="success" lowContrast title={done.lapLai ? 'Thông báo này đã gửi trước đó' : 'Đã gửi'}
            subtitle={`${done.soNguoi} người chơi, ${done.soSocket} kết nối.${done.lapLai ? ' Không gửi lần hai.' : ''}`} onClose={() => setDone(null)} />
        ) : null}
        {!open && push.error ? (unknown
          ? <InlineNotification kind="warning" lowContrast title="Chưa chắc đã gửi" subtitle={(push.error as Error).message} />
          : <InlineError error={push.error} />) : null}
      </div>
      <section className="client-preview" aria-label="Xem trước">
        <p className="muted">Người chơi sẽ thấy</p>
        {kieu === 'popup' ? (
          <div className="client-popup"><h3>Thông báo</h3><p>{noiDung || 'Nội dung popup'}</p><span className="client-popup__ok">ĐỒNG Ý</span></div>
        ) : (
          <div className="marquee"><span>{noiDung || 'Nội dung chữ chạy'}</span></div>
        )}
      </section>
      <Modal open={open} modalHeading={kieu === 'popup' ? 'Gửi popup ngay' : 'Gửi chữ chạy ngay'} primaryButtonText="Gửi" secondaryButtonText="Hủy"
        primaryButtonDisabled={push.isPending} onRequestClose={() => setOpen(false)} onRequestSubmit={() => push.mutate()}>
        <p>Tới: <strong>{uid ? 'người chơi ' + uid : 'mọi người đang online'}</strong>{kieu === 'chuChay' && lan ? <>, chạy <strong>{lan} lần</strong></> : null}</p>
        <p className="pre">{noiDung.trim()}</p>
        {push.error ? (unknown ? <InlineNotification kind="warning" lowContrast hideCloseButton title="Chưa chắc đã gửi" subtitle={(push.error as Error).message} /> : <InlineError error={push.error} />) : null}
      </Modal>
    </div>
  );
}
